import { useState, lazy, Suspense } from 'react';
import { Button } from '@shared-ui/components';
import { adminApi } from '../../api/adminApi';
import type { ConfigSectionName } from '../../types/settings';
import { ALL_SECTION_NAMES } from '../../types/settings';

const ImportConfigDialog = lazy(() =>
  import('./ImportConfigDialog').then(m => ({ default: m.ImportConfigDialog }))
);

const SECTION_LABELS: Record<ConfigSectionName, string> = {
  prompts: 'Prompts',
  providers: 'Providers',
  providerTieredSets: 'Tiered Provider Sets',
  providerParallelSets: 'Parallel Provider Sets',
  pipelineConfigs: 'Pipeline Configs',
  appSettings: 'App Settings',
  whatsappSettings: 'WhatsApp Settings',
  // Rules only — how a notification is addressed and worded. Rollout stays out
  // of the bundle on purpose; see notification-registry-and-rollout.md.
  notificationRules: 'Notification Rules',
};

export function ConfigSync() {
  const [selectedSections, setSelectedSections] = useState<Set<ConfigSectionName>>(
    new Set(ALL_SECTION_NAMES)
  );
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);

  const toggleSection = (section: ConfigSectionName) => {
    setSelectedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const sections = [...selectedSections];
      const bundle = await adminApi.exportConfig(sections.length === ALL_SECTION_NAMES.length ? undefined : sections);

      // Download as JSON file
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().slice(0, 10);
      a.download = `config-${bundle.environment}-${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="settings-section">
      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
        {/* Export Config */}
        <div style={{ flex: '1 1 400px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: 15, color: 'var(--text-primary)' }}>Export Config</h3>
          <p style={{ margin: '0 0 16px 0', fontSize: 13, color: 'var(--text-tertiary)' }}>
            Select sections to include in the export bundle.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {ALL_SECTION_NAMES.map(section => (
              <label key={section} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={selectedSections.has(section)}
                  onChange={() => toggleSection(section)}
                />
                {SECTION_LABELS[section]}
              </label>
            ))}
          </div>
          <Button
            onClick={handleExport}
            disabled={selectedSections.size === 0}
            loading={exporting}
          >
            Export Config
          </Button>
          {exportError && (
            <p style={{ color: 'var(--color-error)', fontSize: 13, marginTop: 8 }}>{exportError}</p>
          )}
        </div>

        {/* Import */}
        <div style={{ flex: '1 1 400px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: 15, color: 'var(--text-primary)' }}>Import</h3>
          <p style={{ margin: '0 0 16px 0', fontSize: 13, color: 'var(--text-tertiary)' }}>
            Upload a config bundle exported from another environment to review and apply changes.
          </p>
          {/* The shared component, not a bare .btn: a page stylesheet declares a
              global `.btn { border: none }`, so the outline variant rendered as
              plain text — an action nobody could see was clickable. */}
          <Button variant="secondary" onClick={() => setShowImport(true)}>
            Import Config…
          </Button>
        </div>
      </div>

      {showImport && (
        <Suspense fallback={null}>
          <ImportConfigDialog onClose={() => setShowImport(false)} />
        </Suspense>
      )}
    </div>
  );
}
